import { TYPE_COLORS, TYPE_NAMES_RU, getDeviceStatus, getDeviceThermalPct } from './equipmentTypes';
import type { RackEquipmentItem } from './rackTypes';

interface DeviceTooltipProps {
  eq: RackEquipmentItem;
  clientX: number;
  clientY: number;
}

/**
 * Fixed-position tooltip that escapes overflow:hidden parents.
 * Uses viewport (clientX/Y) coordinates — NOT element-relative.
 */
export function DeviceTooltip({ eq, clientX, clientY }: DeviceTooltipProps) {
  const TOOLTIP_W = 220;
  const TOOLTIP_MARGIN = 12;
  const VIEWPORT_W = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const VIEWPORT_H = typeof window !== 'undefined' ? window.innerHeight : 1080;

  // Prefer right of cursor, flip left if no space
  const rawLeft = clientX + 15;
  const left = rawLeft + TOOLTIP_W > VIEWPORT_W - TOOLTIP_MARGIN
    ? Math.max(TOOLTIP_MARGIN, clientX - TOOLTIP_W - 10)
    : rawLeft;

  // Prefer above cursor, clamp to viewport
  const rawTop = clientY - 60;
  const top = rawTop < TOOLTIP_MARGIN
    ? clientY + 15
    : Math.min(rawTop, VIEWPORT_H - 200);

  const bgColor = TYPE_COLORS[eq.equipment_type] || TYPE_COLORS.unknown;
  const status = getDeviceStatus(eq.status);
  const thermalPct = getDeviceThermalPct(eq);

  return (
    <div
      className="fixed z-[9999] pointer-events-none select-none"
      style={{ left, top, width: TOOLTIP_W }}
    >
      <div className="bg-slate-900 border border-slate-600 rounded-lg shadow-2xl shadow-black/60 overflow-hidden text-[11px] font-mono">
        {/* Header */}
        <div
          className="px-2.5 py-1.5 flex items-center gap-2"
          style={{ borderBottom: `1px solid ${bgColor}40`, background: `${bgColor}12` }}
        >
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: bgColor, boxShadow: `0 0 6px ${bgColor}80` }}
          />
          <span className="font-bold text-slate-100 truncate">{eq.name}</span>
        </div>

        {/* Body */}
        <div className="px-2.5 py-2 space-y-1">
          <div className="flex justify-between">
            <span className="text-slate-500">Тип</span>
            <span className="text-slate-200 font-medium">{TYPE_NAMES_RU[eq.equipment_type] || eq.equipment_type}</span>
          </div>
          {Boolean(eq.tenant || (eq.attributes as any)?.tenant) && (eq.tenant || (eq.attributes as any)?.tenant) !== 'mine' && (
            <div className="flex justify-between">
              <span className="text-slate-500">Владелец</span>
              <span className="text-purple-300 font-medium truncate max-w-[120px]">{eq.tenant || (eq.attributes as any)?.tenant}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-slate-500">Позиция</span>
            <span className="text-cyan-400 font-bold">
              U{eq.u_position}–U{eq.u_position + eq.u_height - 1}
              <span className="text-slate-500 font-normal"> ({eq.u_height}U)</span>
            </span>
          </div>
          {eq.power_w !== undefined && eq.power_w !== null && (
            <div className="flex justify-between">
              <span className="text-slate-500">Питание</span>
              <span className="text-amber-400 font-medium">{eq.power_w} Вт</span>
            </div>
          )}
          {eq.weight_kg !== undefined && eq.weight_kg !== null && (
            <div className="flex justify-between">
              <span className="text-slate-500">Масса</span>
              <span className="text-emerald-400 font-medium">{eq.weight_kg} кг</span>
            </div>
          )}

          {/* Status + Thermal */}
          <div className="flex justify-between items-center pt-0.5 border-t border-slate-800 mt-1">
            <div className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${
                status === 'critical' ? 'bg-red-500' :
                status === 'warning'  ? 'bg-amber-500' : 'bg-emerald-400'
              }`} />
              <span className={
                status === 'critical' ? 'text-red-400' :
                status === 'warning'  ? 'text-amber-400' : 'text-emerald-400'
              }>
                {eq.status || 'Active'}
              </span>
            </div>
            <div className="text-[10px] text-slate-500">
              Тепл: <span className={thermalPct >= 80 ? 'text-red-400' : thermalPct >= 50 ? 'text-amber-400' : 'text-cyan-400'}>{thermalPct}%</span>
            </div>
          </div>

          <p className="text-[9px] text-slate-600 pt-0.5">Клик → паспорт устройства</p>
        </div>
      </div>
    </div>
  );
}
